import SuperadminService from "@/services/SuperadminService";

const moduleSuperadmin = {
  namespaced: true,
  state: {
    // System Statistics
    systemStats: {
      users: {},
      clinics: {},
      patients: {},
      appointments: {},
      receipts: {},
      recentActivity: {}
    },
    systemStatsLoading: false,

    // User Management
    userSearchResults: {
      users: [],
      searchCriteria: {},
      pagination: {
        total: 0,
        limit: 20,
        offset: 0,
        totalPages: 0
      }
    },
    userSearchLoading: false,
    selectedUser: null,
    userDetailsLoading: false,

    // Clinic Management  
    clinicSearchResults: {
      clinics: [],
      searchCriteria: {},
      pagination: {
        total: 0,
        limit: 20,
        offset: 0,
        totalPages: 0
      }
    },
    clinicSearchLoading: false,
    selectedClinic: null,
    clinicDetailsLoading: false,

    // Authentication
    isSuperadmin: false,
    superadminCheckLoading: false,

    // Clinic/Branch Creation
    createClinicLoading: false,
    createBranchLoading: false,
    lastCreationResult: null,

    // Patient Import
    importPatientsLoading: false,
    lastImportResult: null,
    importTreatmentsLoading: false,

    // UI State
    lastError: null
  },

  mutations: {
    // System Statistics
    setSystemStats(state, data) {
      state.systemStats = data;
    },
    setSystemStatsLoading(state, loading) {
      state.systemStatsLoading = loading;
    },

    // User Management
    setUserSearchResults(state, data) {
      state.userSearchResults = {
        users: data.users || [],
        searchCriteria: data.searchCriteria || {},
        pagination: data.pagination || {}
      };
    },
    setUserSearchLoading(state, loading) {
      state.userSearchLoading = loading;
    },
    setSelectedUser(state, user) {
      state.selectedUser = user;
    },
    setUserDetailsLoading(state, loading) {
      state.userDetailsLoading = loading;
    },
    updateUserInResults(state, updatedUser) {
      const index = state.userSearchResults.users.findIndex(user => user.uid === updatedUser.uid);
      if (index !== -1) {
        state.userSearchResults.users.splice(index, 1, updatedUser);
      }
    },

    // Clinic Management
    setClinicSearchResults(state, data) {
      // Transform clinic data to flatten branches as individual items
      const branches = [];
      
      (data.clinics || []).forEach(clinic => {
        if (clinic.branches && clinic.branches.length > 0) {
          clinic.branches.forEach(branch => {
            branches.push({
              // Branch specific data
              branchId: branch.branchId,
              branchCode: branch.branchCode,
              branchNameTh: branch.branchNameTh,
              branchNameEn: branch.branchNameEn,
              address: branch.address,
              tel: branch.tel,
              email: branch.email,
              status: branch.status,

              // แพ็กที่สาขานี้ถืออยู่ — null ได้เมื่อ BE อ่านข้อมูล billing ไม่สำเร็จ
              // ซึ่งต่างจากแพ็กฟรีที่มาเป็น { planCode: "FREE" } ตารางแยกสองกรณีนี้อยู่
              plan: branch.plan || null,

              // Parent clinic information
              clinicCode: clinic.clinicCode,
              clinicNameTh: clinic.clinicNameTh,
              clinicNameEn: clinic.clinicNameEn,
              
              // Member count for this branch
              memberCount: branch.members ? branch.members.length : 0,
              members: branch.members || [],
              
              // Use clinic creation date if branch doesn't have one
              creationDt: branch.creationDt || clinic.creationDt,
              
              // Keep reference to parent clinic
              parentClinic: {
                clinicCode: clinic.clinicCode,
                clinicNameTh: clinic.clinicNameTh,
                clinicNameEn: clinic.clinicNameEn,
                statistics: clinic.statistics
              }
            });
          });
        } else {
          // If clinic has no branches, create a placeholder entry
          branches.push({
            branchId: null,
            plan: null,
            branchCode: clinic.clinicCode,
            branchNameTh: clinic.clinicNameTh,
            branchNameEn: clinic.clinicNameEn,
            address: clinic.address,
            tel: clinic.tel,
            email: clinic.email,
            status: 1, // Default active status
            
            clinicCode: clinic.clinicCode,
            clinicNameTh: clinic.clinicNameTh,
            clinicNameEn: clinic.clinicNameEn,
            
            memberCount: clinic.statistics?.totalMembers || 0,
            members: [],
            creationDt: clinic.creationDt,
            
            parentClinic: {
              clinicCode: clinic.clinicCode,
              clinicNameTh: clinic.clinicNameTh,
              clinicNameEn: clinic.clinicNameEn,
              statistics: clinic.statistics
            }
          });
        }
      });

      state.clinicSearchResults = {
        branches: branches,
        clinics: data.clinics || [], // Keep original clinic data for reference
        searchCriteria: data.searchCriteria || {},
        pagination: {
          ...data.pagination,
          total: branches.length // Update total to reflect branch count
        }
      };
    },
    setClinicSearchLoading(state, loading) {
      state.clinicSearchLoading = loading;
    },
    setSelectedClinic(state, clinic) {
      state.selectedClinic = clinic;
    },
    setClinicDetailsLoading(state, loading) {
      state.clinicDetailsLoading = loading;
    },

    // Authentication
    setIsSuperadmin(state, isSuperadmin) {
      state.isSuperadmin = isSuperadmin;
    },
    setSuperadminCheckLoading(state, loading) {
      state.superadminCheckLoading = loading;
    },

    // Clinic/Branch Creation
    setCreateClinicLoading(state, loading) {
      state.createClinicLoading = loading;
    },
    setCreateBranchLoading(state, loading) {
      state.createBranchLoading = loading;
    },
    setLastCreationResult(state, result) {
      state.lastCreationResult = result;
    },
    setImportPatientsLoading(state, loading) {
      state.importPatientsLoading = loading;
    },
    setLastImportResult(state, result) {
      state.lastImportResult = result;
    },
    setImportTreatmentsLoading(state, loading) {
      state.importTreatmentsLoading = loading;
    },

    // Error Handling
    setLastError(state, error) {
      state.lastError = error;
    },
    clearLastError(state) {
      state.lastError = null;
    }
  },

  actions: {
    // Admin MCP connector: the panel keeps its own loading/error state.
    async fetchMcpAdmin() {
      const response = await SuperadminService.getMcpAdmin();
      return response.data.data;
    },

    async revokeMcpAdminConnection(_ctx, connectionId) {
      const response = await SuperadminService.revokeMcpAdminConnection(connectionId);
      return response.data.data;
    },

    // System Statistics
    async fetchSystemStats({ commit }) {
      commit('setSystemStatsLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.getSystemStats();
        commit('setSystemStats', response.data.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setSystemStatsLoading', false);
      }
    },

    // Authentication
    async checkSuperadminAccess({ commit }) {
      commit('setSuperadminCheckLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.checkHealth();
        const isSuperadmin = response.data.success === true;
        commit('setIsSuperadmin', isSuperadmin);
        return isSuperadmin;
      } catch (error) {
        commit('setIsSuperadmin', false);
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setSuperadminCheckLoading', false);
      }
    },

    // User Management
    async searchUsers({ commit }, searchParams = {}) {
      commit('setUserSearchLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.searchUsers(searchParams);
        commit('setUserSearchResults', response.data.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setUserSearchLoading', false);
      }
    },

    async fetchUserDetails({ commit }, userId) {
      commit('setUserDetailsLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.getUserDetails(userId);
        commit('setSelectedUser', response.data.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setUserDetailsLoading', false);
      }
    },

    async updateUserStatus({ commit, dispatch }, { userId, statusData }) {
      commit('clearLastError');
      try {
        const response = await SuperadminService.updateUserStatus(userId, statusData);
        
        // Update the user in search results if present
        const updatedUser = response.data.data.user;
        commit('updateUserInResults', updatedUser);
        
        // Update selected user if it's the same user
        if (commit.state?.selectedUser?.uid === userId) {
          commit('setSelectedUser', { ...commit.state.selectedUser, ...updatedUser });
        }

        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      }
    },

    // Clinic Management
    async searchClinics({ commit }, searchParams = {}) {
      commit('setClinicSearchLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.searchClinics(searchParams);
        commit('setClinicSearchResults', response.data.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setClinicSearchLoading', false);
      }
    },

    async fetchClinicDetails({ commit }, clinicCode) {
      commit('setClinicDetailsLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.getClinicDetails(clinicCode);
        commit('setSelectedClinic', response.data.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setClinicDetailsLoading', false);
      }
    },

    // Utility Actions
    clearUserSearch({ commit }) {
      commit('setUserSearchResults', {
        users: [],
        searchCriteria: {},
        pagination: { total: 0, limit: 20, offset: 0, totalPages: 0 }
      });
    },

    clearClinicSearch({ commit }) {
      commit('setClinicSearchResults', {
        clinics: [],
        searchCriteria: {},
        pagination: { total: 0, limit: 20, offset: 0, totalPages: 0 }
      });
    },

    clearSelectedUser({ commit }) {
      commit('setSelectedUser', null);
    },

    clearSelectedClinic({ commit }) {
      commit('setSelectedClinic', null);
    },

    // Clinic/Branch Creation Actions
    async createClinic({ commit }, formData) {
      commit('setCreateClinicLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.createClinic(formData);
        commit('setLastCreationResult', response.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setCreateClinicLoading', false);
      }
    },

    async createBranch({ commit }, formData) {
      commit('setCreateBranchLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.createBranch(formData);
        commit('setLastCreationResult', response.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setCreateBranchLoading', false);
      }
    },

    clearLastCreationResult({ commit }) {
      commit('setLastCreationResult', null);
    },

    async importTreatments({ commit }, formData) {
      commit('setImportTreatmentsLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.importTreatments(formData);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setImportTreatmentsLoading', false);
      }
    },

    async importPatients({ commit }, formData) {
      commit('setImportPatientsLoading', true);
      commit('clearLastError');
      try {
        const response = await SuperadminService.importPatients(formData);
        commit('setLastImportResult', response.data);
        return response.data;
      } catch (error) {
        commit('setLastError', error);
        throw error;
      } finally {
        commit('setImportPatientsLoading', false);
      }
    },

    clearLastImportResult({ commit }) {
      commit('setLastImportResult', null);
    }
  },

  getters: {
    // System Statistics
    getSystemStats: (state) => state.systemStats,
    isSystemStatsLoading: (state) => state.systemStatsLoading,

    // User Management
    getUserSearchResults: (state) => state.userSearchResults,
    isUserSearchLoading: (state) => state.userSearchLoading,
    getSelectedUser: (state) => state.selectedUser,
    isUserDetailsLoading: (state) => state.userDetailsLoading,
    
    getUserSearchPagination: (state) => state.userSearchResults.pagination,
    getUserSearchCriteria: (state) => state.userSearchResults.searchCriteria,

    // Clinic Management
    getClinicSearchResults: (state) => state.clinicSearchResults,
    isClinicSearchLoading: (state) => state.clinicSearchLoading,
    getSelectedClinic: (state) => state.selectedClinic,
    isClinicDetailsLoading: (state) => state.clinicDetailsLoading,
    
    getClinicSearchPagination: (state) => state.clinicSearchResults.pagination,
    getClinicSearchCriteria: (state) => state.clinicSearchResults.searchCriteria,

    // Authentication
    getIsSuperadmin: (state) => state.isSuperadmin,
    isSuperadminCheckLoading: (state) => state.superadminCheckLoading,

    // Error Handling
    getLastError: (state) => state.lastError,
    hasError: (state) => state.lastError !== null,

    // Computed Values
    getTotalUsers: (state) => state.systemStats.users?.totalUsers || 0,
    getTotalClinics: (state) => state.systemStats.clinics?.totalClinics || 0,
    getActiveUsers: (state) => state.systemStats.users?.activeUsers || 0,
    
    // Helper Functions
    getUserById: (state) => (userId) => {
      return state.userSearchResults.users.find(user => user.uid === userId);
    },
    
    getClinicByCode: (state) => (clinicCode) => {
      return state.clinicSearchResults.clinics.find(clinic => clinic.clinicCode === clinicCode);
    },

    // Clinic/Branch Creation
    isCreateClinicLoading: (state) => state.createClinicLoading,
    isCreateBranchLoading: (state) => state.createBranchLoading,
    getLastCreationResult: (state) => state.lastCreationResult,

    // Patient Import
    isImportPatientsLoading: (state) => state.importPatientsLoading,
    isImportTreatmentsLoading: (state) => state.importTreatmentsLoading,
    getLastImportResult: (state) => state.lastImportResult
  }
};

export default moduleSuperadmin;